import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { useAuth } from '../../../context/auth-context'
import { PerModal } from '../../../components/custom_components/Modal'
import { FormItem, InputNumber, FormikTextArea } from '../../../components/formik-component'
import { FormikPaymentCycleDropDown } from '../../../components/formik-component/PaymentCycleDropDown'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerText from '../../../components/custom_components/Text'

import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import useMutationTargetCorrectionPayment from '../../../graphql/target-correction-payment/hooks/useMutationTargetCorrectionPayment'
import { FormikTargetCorrectionPaymentCycleDropDown } from '../../../components/formik-component/TargetCorrectionPaymentDropDown'
import { TargetCorrectionPaymentDto, TargetCorrectionPaymentInput, TargetCorrectionPaymentAllowedYearsDto } from '../../../types'
import { getTargetCorrectionPaymentValidation } from './targetCorrectionPaymentValidation'
import { useCurrency } from '../../../context/currency-context'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import PerFlexRow from '../../../components/custom_components/FlexRow'
interface Props {
  open: boolean
  isDeleting: boolean
  targetCorrectionPayment?: TargetCorrectionPaymentDto
  meetingId: number
  nextPaymentcycle: string
  toggleDeleting: React.Dispatch<React.SetStateAction<boolean>>
  toggleOpen: React.Dispatch<React.SetStateAction<boolean>>
  targetCorrectionPaymentAllowedYears: TargetCorrectionPaymentAllowedYearsDto
  appraisalSeasonYear: number
}

export const TargetCorrectionPaymentModal: FC<Props> = ({
  open,
  isDeleting,
  targetCorrectionPayment,
  toggleDeleting,
  toggleOpen,
  meetingId,
  nextPaymentcycle,
  targetCorrectionPaymentAllowedYears,
  appraisalSeasonYear,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { currency } = useCurrency()
  const { formatCurrencySymbol } = useAmountAndCurrency(user.locale)

  const [error, setError] = useState<string | null>(null)

  // to do technically not correct here it could be that reviewer and managers are in different countries and use different currencies
  const { deleteTargetCorrectionPayment, saveTargetCorrectionPayment } = useMutationTargetCorrectionPayment()

  const mapPropsToInitialState = () => {
    return {
      comment: targetCorrectionPayment ? targetCorrectionPayment.comment : '',
      resultAmount: targetCorrectionPayment ? targetCorrectionPayment.resultAmount : '',
      paymentDate: targetCorrectionPayment ? targetCorrectionPayment.paymentDate : nextPaymentcycle,
      referenceYear: targetCorrectionPayment
        ? targetCorrectionPayment.referenceYear
        : targetCorrectionPaymentAllowedYears.currentYearAllowed
        ? appraisalSeasonYear
        : appraisalSeasonYear - 1,
      meetingId: meetingId,
      id: targetCorrectionPayment ? targetCorrectionPayment.id : undefined,
    } as TargetCorrectionPaymentInput
  }
  const [initialFormState, setInitialFormState] = useState(mapPropsToInitialState())

  useEffect(() => {
    setInitialFormState(mapPropsToInitialState())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCorrectionPayment])

  const getNotificationOperation = () => {
    if (isDeleting) {
      return t('appraisalMeeting.targetCorrectionPayment.notifications.operations.deleted')
    }
    if (!!targetCorrectionPayment && !!targetCorrectionPayment.id) {
      return t('appraisalMeeting.targetCorrectionPayment.notifications.operations.edited')
    } else {
      return t('appraisalMeeting.targetCorrectionPayment.notifications.operations.created')
    }
  }

  const saveOrUpdateTargetCorrection = async (values: {
    comment: string
    paymentDate: string
    resultAmount: number
    referenceYear: number
  }) => {
    const targetCorrectionPaymentInput: TargetCorrectionPaymentInput = {
      meetingId: meetingId,
      comment: values.comment,
      paymentDate: values.paymentDate,
      referenceYear: Number(values.referenceYear),
      resultAmount: values.resultAmount,
      id: targetCorrectionPayment ? targetCorrectionPayment.id : undefined,
    }
    const targetCorrection = await saveTargetCorrectionPayment(targetCorrectionPaymentInput, meetingId)
    if (!!targetCorrection.errors && !!targetCorrection.errors.length) {
      setError('appraisalMeeting.targetCorrectionPayment.notifications.saveErrorMessage')
    } else {
      showNotification({
        type: 'success',
        message: t('appraisalMeeting.targetCorrectionPayment.notifications.success.title'),
        description: t('appraisalMeeting.targetCorrectionPayment.notifications.success.description', {
          operation: getNotificationOperation(),
        }),
      })
      toggleOpen(false)
    }
  }

  const deleteTargetCorrection = async (): Promise<void> => {
    if (!targetCorrectionPayment) {
      return
    }
    const { errors } = await deleteTargetCorrectionPayment(targetCorrectionPayment.id, targetCorrectionPayment.meetingId)
    if (!!errors && !!errors.length) {
      setError('appraisalMeeting.targetCorrectionPayment.notifications.deletionErrorMessage')
    } else {
      showNotification({
        type: 'success',
        message: t('appraisalMeeting.targetCorrectionPayment.notifications.success.title'),
        description: t('appraisalMeeting.targetCorrectionPayment.notifications.success.description', {
          operation: getNotificationOperation(),
        }),
      })
      toggleDeleting(false)
      toggleOpen(false)
    }
  }

  return (
    <Formik
      initialValues={{ ...initialFormState }}
      enableReinitialize={true}
      onSubmit={async (
        values: { comment: string; paymentDate: string; resultAmount: number; referenceYear: number },
        { setSubmitting, resetForm }: any,
      ) => {
        if (isDeleting) {
          await deleteTargetCorrection()
        } else {
          await saveOrUpdateTargetCorrection(values)
        }
        resetForm()
        setSubmitting(false)
      }}
      validateOnBlur={true}
      validateOnChange={false}
      validationSchema={getTargetCorrectionPaymentValidation(formatCurrencySymbol, currency, t)}
    >
      {(props) => {
        const { isSubmitting, handleSubmit, values, resetForm } = props

        return (
          <PerModal
            title={{ heading: t('appraisalMeeting.targetCorrectionPayment.title') }}
            visible={open}
            onCancel={() => {
              resetForm()
              toggleDeleting(false)
              toggleOpen(false)
            }}
            footer={{
              formikForm: {
                onSubmit: handleSubmit,
              },
              confirmBtn: {
                text: isDeleting ? t('global.button.delete') : t('global.button.save'),
                disabled: isSubmitting,
                isProcessing: { active: isSubmitting },
              },
            }}
          >
            <div>
              {isDeleting && (
                <PerSpacer mb="l">
                  <PerFlexRow>
                    <PerText fontWeight="bold">{t('appraisalMeeting.targetCorrectionPayment.deleteDescription')}</PerText>
                  </PerFlexRow>
                </PerSpacer>
              )}
              <PerSpacer mb="xs">
                <PerText appearance="label-small" display="block" mb="xxxs">
                  {t('appraisalMeeting.targetCorrectionPayment.paymentDate')}:
                </PerText>
                <FormItem name="paymentDate">
                  <FormikPaymentCycleDropDown name="paymentDate" earliestCycle={nextPaymentcycle} disabled={isDeleting} />
                </FormItem>
              </PerSpacer>

              <PerSpacer mb="xs">
                <PerText appearance="label-small" display="block" mb="xxxs">
                  {t('appraisalMeeting.targetCorrectionPayment.referenceYear')}:
                </PerText>
                <FormItem name="referenceYear">
                  <FormikTargetCorrectionPaymentCycleDropDown
                    name="referenceYear"
                    value={`${values.referenceYear}`}
                    currentAppraisalSeasonYear={appraisalSeasonYear}
                    targetCorrectionPaymentAllowedYears={targetCorrectionPaymentAllowedYears}
                    disabled={isDeleting}
                  />
                </FormItem>
              </PerSpacer>

              <PerSpacer mb="xs">
                <PerText appearance="label-small" display="block" mb="xxxs">
                  {t('appraisalMeeting.targetCorrectionPayment.toBePaid')}:
                </PerText>
                <FormItem name="resultAmount">
                  <InputNumber
                    name="resultAmount"
                    type="currency"
                    timezoneAndLocale={user}
                    currency={currency}
                    step={50}
                    min={0}
                    placeholder="0"
                    disabled={isDeleting}
                  />
                </FormItem>
              </PerSpacer>

              <div>
                <PerText appearance="label-small" display="block" mb="xxxs">
                  {t('appraisalMeeting.targetCorrectionPayment.comment')}:
                </PerText>
                <FormItem name="comment">
                  {isDeleting && !!targetCorrectionPayment && !!targetCorrectionPayment.comment && (
                    <div className="per-noteItem--text" data-e2e-noteslist>
                      <PerText appearance="flowing-text" mt="xxs">
                        {targetCorrectionPayment.comment}
                      </PerText>
                    </div>
                  )}
                  {!isDeleting && (
                    <FormikTextArea
                      placeholder={t('appraisalMeeting.targetCorrectionPayment.commentPlaceholder')}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      name="comment"
                    />
                  )}
                </FormItem>
              </div>
              {error && (
                <PerText color="error" fontSize="m" mt="m">
                  {error}
                </PerText>
              )}
            </div>
          </PerModal>
        )
      }}
    </Formik>
  )
}
